import CustomScrollbars from "./CustomScrollbars";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import UnsavedPlatformDraftsDialog from "components/userManagement/UnsavedPlatformDraftsDialog";
import {
  clearPersistedPlatformUserSelection,
  clearPlatformUserDrafts,
  hasAnyPlatformUserDrafts,
  loadPlatformUserDrafts,
  summarizeDraftsForDialog,
} from "userManagement/platformDraftsStorage";

import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import {
  ArrowDropDown,
  LoginSharp,
  LogoutSharp,
  Menu as MenuIcon,
  Person,
  Settings,
} from "@mui/icons-material";
import { Badge } from "@mui/material";

import {
  CHCM_LOGO_MARK_WHITE_URL,
  TOP_BAR_LOGO_HEIGHT_PX,
  SHELL_TOP_BAR_PX,
  SHELL_SIDEBAR_WIDTH_PX,
  SHELL_SIDEBAR_WIDTH_2K_PX,
  SHELL_NAV_ROW_HEIGHT_PX,
  SHELL_NAV_ROW_HEIGHT_2K_PX,
  SHELL_NAV_GAP_PX,
  SHELL_NAV_ROW_INNER_SPACING,
  VIEWPORT_2K_MIN_MEDIA,
  VIEWPORT_4K_MIN_MEDIA,
  SHELL_SIDEBAR_WIDTH_4K_PX,
  SHELL_NAV_ROW_HEIGHT_4K_PX,
} from "config";
import { useLogoutMutation } from "features/auth/auth";
import { getIntegrationsSettingsUnsaved } from "features/integrationsBeta/integrationsSettingsUnsaved";
import { getSftpNavigationUnsaved } from "features/sftp/sftpNavigationUnsaved";
import { useAppDispatch, useAppSelector } from "features/store";
import {
  getManagementMenu,
  getSidebarNav,
  isNavPathActive,
  type NavKey,
} from "utils/navModel";
import { MANAGEMENT_ICONS, SIDEBAR_ICONS } from "components/common/navIcons";
import { canSeeSecureExchange, isClientPortalUser } from "utils/roles";
import { pushNotification } from "features/notificationSlice";
import { useGetSecureUnreadCountQuery } from "features/secureUploads/secureUploads";

type HeaderProps = {
  children?: React.ReactNode;
};

const Header = ({ children }: HeaderProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const [logout] = useLogoutMutation();

  const location = useLocation();
  const navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [anchorMgmt, setAnchorMgmt] = useState<null | HTMLElement>(null);
  const mgmtMenuOpen = Boolean(anchorMgmt);
  const [unsavedDraftsOpen, setUnsavedDraftsOpen] = useState(false);
  const [unsavedDraftRows, setUnsavedDraftRows] = useState<
    ReturnType<typeof summarizeDraftsForDialog>
  >([]);
  const [pendingAfterDiscard, setPendingAfterDiscard] = useState<
    null | { kind: "navigate"; path: string } | { kind: "logout" }
  >(null);
  const [sftpUnsavedOpen, setSftpUnsavedOpen] = useState(false);
  const [pendingSftpAfterDiscard, setPendingSftpAfterDiscard] = useState<
    null | { kind: "navigate"; path: string } | { kind: "logout" }
  >(null);
  const [integrationsUnsavedOpen, setIntegrationsUnsavedOpen] = useState(false);
  const [pendingIntegrationsAfterDiscard, setPendingIntegrationsAfterDiscard] =
    useState<null | { kind: "navigate"; path: string } | { kind: "logout" }>(
      null,
    );

  const pp = user?.platform_profile;
  const navRows = useMemo(() => getSidebarNav(pp), [pp]);
  const managementItems = useMemo(() => getManagementMenu(pp), [pp]);
  const showSecureFileUpload = canSeeSecureExchange(pp);

  const [isTabVisible, setIsTabVisible] = useState(
    typeof document === "undefined" || document.visibilityState !== "hidden",
  );
  useEffect(() => {
    const onVisibility = () =>
      setIsTabVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const { data: secureUnread, refetch: refetchSecureUnread } =
    useGetSecureUnreadCountQuery(undefined, {
      skip: !isAuthenticated || !showSecureFileUpload,
      pollingInterval: isTabVisible ? 30000 : 0,
    });

  useEffect(() => {
    if (isTabVisible && isAuthenticated && showSecureFileUpload) {
      refetchSecureUnread();
    }
  }, [
    isTabVisible,
    isAuthenticated,
    showSecureFileUpload,
    refetchSecureUnread,
  ]);
  const secureUnreadCount = secureUnread?.unread ?? 0;

  useEffect(() => {
    if (!isAuthenticated || !showSecureFileUpload || secureUnread == null)
      return;
    const SEEN_KEY = `secureUnreadSeen:${user?.id ?? "anon"}`;
    const count = secureUnread.unread;
    let prev: number | null = null;
    try {
      const raw = sessionStorage.getItem(SEEN_KEY);
      prev = raw == null ? null : parseInt(raw, 10);
    } catch {
      prev = null;
    }

    if (prev == null) {
      if (count > 0) {
        dispatch(
          pushNotification({
            message: `You have ${count} unread file${count > 1 ? "s" : ""} in Secure Exchange.`,
            severity: "info",
            autoHideMs: 10000,
          }),
        );
      }
    } else if (count > prev) {
      const delta = count - prev;
      dispatch(
        pushNotification({
          message: `You received ${delta} new file${delta > 1 ? "s" : ""} in Secure Exchange.`,
          severity: "info",
          autoHideMs: 10000,
        }),
      );
    }

    try {
      sessionStorage.setItem(SEEN_KEY, String(count));
    } catch {}
  }, [secureUnread, isAuthenticated, showSecureFileUpload, user?.id, dispatch]);

  const isResetPasswordRoute = location.pathname === "/reset-password";
  const showAuthenticatedChrome =
    isAuthenticated && user !== null && !isResetPasswordRoute;

  const isActive = useMemo(
    () => ({
      home: location.pathname === "/" || location.pathname === "",
      integrations: isNavPathActive(location.pathname, "/integrations"),
      integrationsBeta: isNavPathActive(
        location.pathname,
        "/integrations-beta",
      ),
      storage: location.pathname === "/data-storage",
      chat: location.pathname === "/chat",
      userManagement: location.pathname === "/user-management",
      sftpManagement: location.pathname.startsWith("/sftp-management"),
      partners: location.pathname.startsWith("/partners"),
      botsManagement: location.pathname.startsWith("/bots-management"),
      secureFileUpload: location.pathname === "/secure-exchange",
    }),
    [location.pathname],
  );

  const navActive: Record<NavKey, boolean> = {
    home: isActive.home,
    dataStorage: isActive.storage,
    // A client portal user's "Integrations" IS the platform (CAL-5777).
    integrations:
      isActive.integrations ||
      (isActive.integrationsBeta && isClientPortalUser(pp)),
    ai: isActive.chat,
    partnerDirectory: isActive.partners,
    secureExchange: isActive.secureFileUpload,
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => setAnchorEl(null);

  const performLogout = () => {
    logout()
      .unwrap()
      .then(() => navigate("/login"))
      .catch((error) => console.error("Logout failed:", error));
  };

  const openUnsavedDraftsDialog = (
    next: { kind: "navigate"; path: string } | { kind: "logout" },
  ) => {
    setUnsavedDraftRows(summarizeDraftsForDialog(loadPlatformUserDrafts()));
    setPendingAfterDiscard(next);
    setUnsavedDraftsOpen(true);
  };

  const openSftpUnsavedDialog = (
    next: { kind: "navigate"; path: string } | { kind: "logout" },
  ) => {
    setPendingSftpAfterDiscard(next);
    setSftpUnsavedOpen(true);
  };

  const openIntegrationsUnsavedDialog = (
    next: { kind: "navigate"; path: string } | { kind: "logout" },
  ) => {
    setPendingIntegrationsAfterDiscard(next);
    setIntegrationsUnsavedOpen(true);
  };

  const requestNavigate = (path: string) => {
    const onUserManagement = location.pathname === "/user-management";
    const leavingUserManagement =
      onUserManagement && path !== "/user-management";
    if (
      leavingUserManagement &&
      hasAnyPlatformUserDrafts(loadPlatformUserDrafts())
    ) {
      openUnsavedDraftsDialog({ kind: "navigate", path });
      return;
    }

    const onSftp = location.pathname.startsWith("/sftp-management");
    const leavingSftp = onSftp && !path.startsWith("/sftp-management");
    if (leavingSftp && getSftpNavigationUnsaved()) {
      openSftpUnsavedDialog({ kind: "navigate", path });
      return;
    }

    if (getIntegrationsSettingsUnsaved()) {
      openIntegrationsUnsavedDialog({ kind: "navigate", path });
      return;
    }

    navigate(path);
  };

  const onLogout = () => {
    handleMenuClose();
    if (hasAnyPlatformUserDrafts(loadPlatformUserDrafts())) {
      openUnsavedDraftsDialog({ kind: "logout" });
      return;
    }
    if (getSftpNavigationUnsaved()) {
      openSftpUnsavedDialog({ kind: "logout" });
      return;
    }
    if (getIntegrationsSettingsUnsaved()) {
      openIntegrationsUnsavedDialog({ kind: "logout" });
      return;
    }
    performLogout();
  };

  const handleAccountSettings = () => {
    handleMenuClose();
    requestNavigate("/account-settings");
  };

  const closeDrawerAndNavigate = (path: string) => {
    setDrawerOpen(false);
    requestNavigate(path);
  };

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (
      location.pathname === "/user-management" &&
      hasAnyPlatformUserDrafts(loadPlatformUserDrafts())
    ) {
      e.preventDefault();
      requestNavigate("/");
      return;
    }
    if (
      location.pathname.startsWith("/sftp-management") &&
      getSftpNavigationUnsaved()
    ) {
      e.preventDefault();
      requestNavigate("/");
      return;
    }
    if (getIntegrationsSettingsUnsaved()) {
      e.preventDefault();
      requestNavigate("/");
    }
  };

  const clientProfileName = useMemo(() => {
    if (!user) return "Client";
    const fromClientName = (user.first_name ?? "").trim();
    return fromClientName || user.username || "Client";
  }, [user]);

  const navRowSx = (active: boolean, hovered: boolean) => {
    const shActive = theme.palette.calibrate.navRowShadowActive;
    const shHover = theme.palette.calibrate.navRowShadowHover;
    const rowShadow = active ? shActive : hovered ? shHover : "none";
    return {
      minHeight: SHELL_NAV_ROW_HEIGHT_PX,
      height: SHELL_NAV_ROW_HEIGHT_PX,
      p: 0,
      boxSizing: "border-box" as const,
      display: "flex",
      alignItems: "stretch",
      justifyContent: "flex-start",
      cursor: "pointer",
      border: "none",
      width: "100%",
      textAlign: "left" as const,
      fontFamily: theme.typography.calibrateShell.fontFamily,
      fontSize: theme.typography.calibrateShell.fontSize,
      fontWeight: active ? 600 : 500,
      color: theme.palette.calibrate.iconOnTeal,
      bgcolor: active ? theme.palette.calibrate.navActive : "transparent",
      borderRadius: 0,
      transition: "background-color 0.15s ease, box-shadow 0.15s ease",
      boxShadow: rowShadow,
      "&:hover": {
        bgcolor: active
          ? theme.palette.calibrate.navActive
          : theme.palette.calibrate.navHover,
        boxShadow: active ? shActive : shHover,
      },
      [VIEWPORT_2K_MIN_MEDIA]: {
        minHeight: SHELL_NAV_ROW_HEIGHT_2K_PX,
        height: SHELL_NAV_ROW_HEIGHT_2K_PX,
        fontSize: 15,
      },
      [VIEWPORT_4K_MIN_MEDIA]: {
        minHeight: SHELL_NAV_ROW_HEIGHT_4K_PX,
        height: SHELL_NAV_ROW_HEIGHT_4K_PX,
        fontSize: 16,
      },
    };
  };

  const navIconSlotSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    flexShrink: 0,
    lineHeight: 0,
    [VIEWPORT_2K_MIN_MEDIA]: {
      width: 26,
      height: 26,
    },
    [VIEWPORT_4K_MIN_MEDIA]: {
      width: 28,
      height: 28,
    },
  };

  const navRowInnerSx = {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    height: "100%",
    px: SHELL_NAV_ROW_INNER_SPACING,
    boxSizing: "border-box" as const,
    width: "100%",
  };

  const shellNavIconSx = {
    fontSize: 22,
    color: "inherit",
    [VIEWPORT_2K_MIN_MEDIA]: { fontSize: 24 },
    [VIEWPORT_4K_MIN_MEDIA]: { fontSize: 26 },
  };

  const NavRow = ({
    icon,
    label,
    active,
    onClick,
  }: {
    icon: React.ReactNode;
    label: React.ReactNode;
    active: boolean;
    onClick: () => void;
  }) => {
    const [hover, setHover] = useState(false);
    return (
      <Box
        component="button"
        type="button"
        onClick={onClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        sx={navRowSx(active, hover)}
      >
        <Box sx={navRowInnerSx}>
          <Box sx={navIconSlotSx}>{icon}</Box>
          <Typography
            component="span"
            sx={{
              color: "calibrate.navText",
              font: "inherit",
              fontWeight: "inherit",
              lineHeight: theme.typography.calibrateShell.lineHeight,
              display: "flex",
              alignItems: "center",
            }}
          >
            {label}
          </Typography>
        </Box>
      </Box>
    );
  };

  const sidebarNav = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: `${SHELL_NAV_GAP_PX}px`,
        px: 0,
        pb: 2,
      }}
    >
      {navRows.map((row) => {
        const icon = SIDEBAR_ICONS[row.key](shellNavIconSx);
        return (
          <NavRow
            key={row.key}
            icon={
              row.key === "secureExchange" ? (
                <Badge
                  badgeContent={secureUnreadCount}
                  color="error"
                  overlap="circular"
                >
                  {icon}
                </Badge>
              ) : (
                icon
              )
            }
            label={row.label}
            active={navActive[row.key]}
            onClick={() => requestNavigate(row.path)}
          />
        );
      })}
    </Box>
  );

  const drawerContent = (
    <Box
      role="presentation"
      sx={{
        width: "100%",
        minHeight: "100%",
        height: "100%",
        boxSizing: "border-box",
        bgcolor: "primary.main",
        pt: 2,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          display: "flex",
          overflow: "hidden",
          bgcolor: "primary.main",
        }}
      >
        <CustomScrollbars>{sidebarNav}</CustomScrollbars>
      </Box>
      <Divider
        sx={{ borderColor: "rgba(255,255,255,0.2)", my: 1, flexShrink: 0 }}
      />
      <Box
        sx={{
          px: 0,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          flexShrink: 0,
          bgcolor: "primary.main",
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => {
            setDrawerOpen(false);
            handleAccountSettings();
          }}
          sx={navRowSx(false, false)}
        >
          <Box sx={navRowInnerSx}>
            <Box sx={navIconSlotSx}>
              <Settings sx={shellNavIconSx} />
            </Box>
            <Typography
              component="span"
              sx={{
                color: "#fff",
                font: "inherit",
                lineHeight: theme.typography.calibrateShell.lineHeight,
                display: "flex",
                alignItems: "center",
              }}
            >
              Account settings
            </Typography>
          </Box>
        </Box>
        <Box
          component="button"
          type="button"
          onClick={() => {
            setDrawerOpen(false);
            onLogout();
          }}
          sx={{
            ...navRowSx(false, false),
            color: "#FFDADA",
            "&:hover": { bgcolor: "rgba(211,47,47,0.35)" },
          }}
        >
          <Box sx={navRowInnerSx}>
            <Box sx={navIconSlotSx}>
              <LogoutSharp sx={shellNavIconSx} />
            </Box>
            <Typography
              component="span"
              sx={{
                color: "#FFDADA",
                font: "inherit",
                fontWeight: 600,
                lineHeight: theme.typography.calibrateShell.lineHeight,
                display: "flex",
                alignItems: "center",
              }}
            >
              Logout
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const userMenuPaperSx = {
    mt: 1.25,
    minWidth: 220,
    borderRadius: 1,
    border: "1px solid rgba(0,0,0,0.2)",
    boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
  };

  const shellHeaderMenuSlotProps = {
    root: {
      sx: { zIndex: theme.zIndex.tooltip },
    },
  };

  const avatarButton = (
    <IconButton
      onClick={handleMenuOpen}
      aria-controls={menuOpen ? "user-menu" : undefined}
      aria-haspopup="true"
      aria-expanded={menuOpen ? "true" : undefined}
      size="small"
      sx={{
        ml: 1,
        p: 0,
        width: 40,
        height: 40,
        borderRadius: "50%",
        border: "none",
        boxShadow: "none",
        bgcolor: "calibrate.iconOnTeal",
        "&:hover": { bgcolor: "#d4eef5" },
      }}
    >
      <Person sx={{ fontSize: 26, color: "primary.main" }} />
    </IconButton>
  );

  const userDropdownMenu = (
    <Menu
      id="user-menu"
      anchorEl={anchorEl}
      open={menuOpen}
      onClose={handleMenuClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={shellHeaderMenuSlotProps}
      PaperProps={{ sx: userMenuPaperSx }}
      MenuListProps={{ dense: true, sx: { py: 0.5 } }}
    >
      <MenuItem
        onClick={handleAccountSettings}
        sx={{
          py: 1,
          "&:hover": { bgcolor: "rgba(49, 129, 159, 0.1)" },
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          <Settings sx={{ fontSize: 20, color: "text.primary" }} />
        </ListItemIcon>
        <ListItemText
          primary="Account Settings"
          primaryTypographyProps={{
            fontSize: 14,
            fontFamily: theme.typography.fontFamily,
            color: "text.primary",
          }}
        />
      </MenuItem>
      <MenuItem
        onClick={onLogout}
        sx={{
          py: 1,
          "&:hover": { bgcolor: "rgba(255, 218, 218, 0.8)" },
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          <LogoutSharp sx={{ fontSize: 20, color: "error.main" }} />
        </ListItemIcon>
        <ListItemText
          primary="Logout"
          primaryTypographyProps={{
            fontSize: 14,
            fontWeight: 600,
            fontFamily: theme.typography.fontFamily,
            color: "error.main",
          }}
        />
      </MenuItem>
    </Menu>
  );

  const mgmtMenuItemSx = {
    py: 1,
    "&:hover": { bgcolor: theme.palette.calibrate.mgmtMenuItemHover },
  };

  const mgmtMenuItemTypography = {
    fontFamily: theme.typography.calibrateShell.fontFamily,
    fontSize: theme.typography.calibrateShell.fontSize,
    fontWeight: theme.typography.calibrateShell.fontWeight,
    letterSpacing: theme.typography.calibrateShell.letterSpacing,
    lineHeight: theme.typography.calibrateShell.lineHeight,
    color: "rgba(0,0,0,0.87)",
  };

  const managementMenuTriggerLabel: React.ReactNode =
    location.pathname === "/user-management"
      ? "User Management"
      : location.pathname.startsWith("/sftp-management")
        ? "SFTP Management"
        : location.pathname === "/partner-management"
          ? "Partner Management"
          : isActive.integrationsBeta
            ? "Integrations Platform"
            : location.pathname.startsWith("/bots-management")
              ? "Bots Management"
              : null;

  const topBarRight = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        flexShrink: 0,
        pr: "10px",
        minHeight: SHELL_TOP_BAR_PX,
        maxHeight: SHELL_TOP_BAR_PX,
      }}
    >
      {showAuthenticatedChrome ? (
        <>
          {managementItems.length > 0 ? (
            <>
              <Button
                endIcon={<ArrowDropDown sx={{ color: "#fff" }} />}
                onClick={(e) => setAnchorMgmt(e.currentTarget)}
                sx={{
                  color: "#fff",
                  textTransform: "none",
                  ...theme.typography.calibrateShell,
                  display: "inline-flex",
                  alignItems: "center",
                  maxWidth: { xs: 160, sm: "none" },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  "& .MuiButton-label": {
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "2px",
                  },
                }}
              >
                {managementMenuTriggerLabel ? (
                  // The button clips at maxWidth; the ellipsis has to sit on
                  // the text itself, or a long page name is cut mid-letter.
                  <Box
                    component="span"
                    sx={{
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {managementMenuTriggerLabel}
                  </Box>
                ) : (
                  <>
                    <Box
                      component="span"
                      sx={{ display: { xs: "none", sm: "inline" } }}
                    >
                      Management Options
                    </Box>
                    <Box
                      component="span"
                      sx={{ display: { xs: "inline", sm: "none" } }}
                    >
                      Manage
                    </Box>
                  </>
                )}
              </Button>
              <Menu
                anchorEl={anchorMgmt}
                open={mgmtMenuOpen}
                onClose={() => setAnchorMgmt(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={shellHeaderMenuSlotProps}
                PaperProps={{ sx: { ...userMenuPaperSx, minWidth: 240 } }}
              >
                {managementItems.map((item) => (
                  <MenuItem
                    key={item.key}
                    onClick={() => {
                      setAnchorMgmt(null);
                      requestNavigate(item.path);
                    }}
                    sx={mgmtMenuItemSx}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {MANAGEMENT_ICONS[item.key]({
                        fontSize: 20,
                        color: "rgba(0,0,0,0.87)",
                      })}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={mgmtMenuItemTypography}
                    />
                  </MenuItem>
                ))}
              </Menu>
            </>
          ) : (
            <Typography
              sx={{
                color: "#fff",
                ...theme.typography.calibrateShell,
                fontWeight: 500,
                mr: 0.5,
                maxWidth: { xs: "42vw", sm: "none" },
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              Welcome, {clientProfileName}!
            </Typography>
          )}
          {avatarButton}
          {userDropdownMenu}
        </>
      ) : (
        location.pathname !== "/login" &&
        !isResetPasswordRoute &&
        location.pathname !== "/integrations" && (
          <Button
            startIcon={<LoginSharp />}
            href="/login"
            variant="contained"
            sx={{
              textTransform: "none",
              bgcolor: "rgba(255,255,255,0.15)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.4)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
            }}
          >
            Login
          </Button>
        )
      )}
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          component="header"
          sx={{
            width: "100%",
            height: SHELL_TOP_BAR_PX,
            minHeight: SHELL_TOP_BAR_PX,
            maxHeight: SHELL_TOP_BAR_PX,
            flexShrink: 0,
            boxSizing: "border-box",
            bgcolor: "primary.main",
            boxShadow: theme.palette.calibrate.topBarShadow,
            display: "flex",
            alignItems: "center",
            zIndex: theme.zIndex.appBar,
            overflow: "visible",
          }}
        >
          <Toolbar
            disableGutters
            sx={{
              minHeight: SHELL_TOP_BAR_PX,
              maxHeight: SHELL_TOP_BAR_PX,
              height: SHELL_TOP_BAR_PX,
              width: "100%",
              px: 0,
              py: 0,
              display: "flex",
              alignItems: "center",
              boxSizing: "border-box",
              overflow: "visible",
            }}
          >
            {isMobile && showAuthenticatedChrome && (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="open menu"
                onClick={() => setDrawerOpen(true)}
                sx={{ color: "#fff", ml: 0.5, flexShrink: 0 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Link
              to="/"
              onClick={handleLogoClick}
              style={{ display: "flex", alignItems: "center", flexShrink: 0 }}
            >
              <Box
                component="img"
                src={CHCM_LOGO_MARK_WHITE_URL}
                alt="Calibrate HCM"
                sx={{
                  height: TOP_BAR_LOGO_HEIGHT_PX,
                  minHeight: TOP_BAR_LOGO_HEIGHT_PX,
                  maxHeight: TOP_BAR_LOGO_HEIGHT_PX,
                  width: "auto",
                  ml: "10px",
                  cursor: "pointer",
                  display: "block",
                  objectFit: "contain",
                  flexShrink: 0,
                }}
              />
            </Link>
            {location.pathname.startsWith("/data-storage") &&
              window.sessionStorage.getItem("botsReturnTo") != null && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ArrowBackIcon fontSize="small" />}
                  onClick={() => {
                    const target =
                      window.sessionStorage.getItem("botsReturnTo");
                    window.sessionStorage.removeItem("botsReturnTo");
                    if (target) navigate(target);
                  }}
                  sx={{
                    ml: 2,
                    flexShrink: 0,
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.5)",
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      borderColor: "#fff",
                      bgcolor: "rgba(255,255,255,0.08)",
                    },
                  }}
                >
                  Back to Bots Management
                </Button>
              )}
            <Box sx={{ flexGrow: 1, minWidth: 0 }} />
            {topBarRight}
          </Toolbar>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            minHeight: 0,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          {showAuthenticatedChrome && !isMobile && (
            <Box
              component="nav"
              aria-label="Main navigation"
              sx={{
                width: SHELL_SIDEBAR_WIDTH_PX,
                minWidth: SHELL_SIDEBAR_WIDTH_PX,
                maxWidth: SHELL_SIDEBAR_WIDTH_PX,
                [VIEWPORT_2K_MIN_MEDIA]: {
                  width: SHELL_SIDEBAR_WIDTH_2K_PX,
                  minWidth: SHELL_SIDEBAR_WIDTH_2K_PX,
                  maxWidth: SHELL_SIDEBAR_WIDTH_2K_PX,
                },
                [VIEWPORT_4K_MIN_MEDIA]: {
                  width: SHELL_SIDEBAR_WIDTH_4K_PX,
                  minWidth: SHELL_SIDEBAR_WIDTH_4K_PX,
                  maxWidth: SHELL_SIDEBAR_WIDTH_4K_PX,
                },
                flexShrink: 0,
                alignSelf: "stretch",
                bgcolor: "primary.main",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                overflow: "hidden",
                borderRight: "1px solid rgba(0,0,0,0.06)",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  overflow: "hidden",
                  bgcolor: "primary.main",
                }}
              >
                <CustomScrollbars sx={{ pt: 2 }}>{sidebarNav}</CustomScrollbars>
              </Box>
            </Box>
          )}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              bgcolor: "background.default",
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 280,
            bgcolor: "primary.main",
            height: "100%",
            minHeight: "100%",
            backgroundImage: "none",
            overflow: "hidden",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <UnsavedPlatformDraftsDialog
        open={unsavedDraftsOpen}
        onClose={() => {
          setUnsavedDraftsOpen(false);
          setPendingAfterDiscard(null);
        }}
        rows={unsavedDraftRows}
        onDiscardAndContinue={() => {
          clearPlatformUserDrafts();
          clearPersistedPlatformUserSelection();
          setUnsavedDraftsOpen(false);
          const pending = pendingAfterDiscard;
          setPendingAfterDiscard(null);
          if (!pending) return;
          if (pending.kind === "navigate") {
            navigate(pending.path);
          } else {
            performLogout();
          }
        }}
      />

      <UnsavedPlatformDraftsDialog
        open={sftpUnsavedOpen}
        onClose={() => {
          setSftpUnsavedOpen(false);
          setPendingSftpAfterDiscard(null);
        }}
        rows={[]}
        title="Unsaved SFTP changes"
        intro="You have unsaved changes in SFTP Management. Stay to keep editing, or discard them and continue. Unsaved edits will be lost."
        showWarning={true}
        onDiscardAndContinue={() => {
          setSftpUnsavedOpen(false);
          const pending = pendingSftpAfterDiscard;
          setPendingSftpAfterDiscard(null);
          if (!pending) return;
          if (pending.kind === "navigate") {
            navigate(pending.path);
          } else {
            performLogout();
          }
        }}
      />

      <UnsavedPlatformDraftsDialog
        open={integrationsUnsavedOpen}
        onClose={() => {
          setIntegrationsUnsavedOpen(false);
          setPendingIntegrationsAfterDiscard(null);
        }}
        rows={[]}
        title="Unsaved integration settings"
        intro="You have unsaved changes in the integration's Settings. Stay to keep editing, or discard them and continue. Unsaved edits will be lost."
        showWarning={true}
        onDiscardAndContinue={() => {
          setIntegrationsUnsavedOpen(false);
          const pending = pendingIntegrationsAfterDiscard;
          setPendingIntegrationsAfterDiscard(null);
          if (!pending) return;
          if (pending.kind === "navigate") {
            navigate(pending.path);
          } else {
            performLogout();
          }
        }}
      />
    </>
  );
};

export default Header;
